import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSsrBootstrap } from "@/lib/ssr-bootstrap";
import { CURRENCY_STORAGE_KEY, MARKETS_BY_CODE, marketToFormatLocale, countryToMarket } from "./markets";
import { AuthContext } from "@/hooks/useUser";
import { fetchUserSettings, updateUserSettings } from "@/lib/users-api";
import { fxConvert } from "@/lib/fx";
import { useLocale } from "@/i18n/LocaleContext";
import { useTranslation } from "react-i18next";
import {
  formatMoneyWithCurrency,
  formatPriceCode,
  formatChargeDisclosure as baseFormatChargeDisclosure,
} from "@/lib/formatMoney";

export interface Currency {
  code: string;
  symbol: string;
  label: string;
}

export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: "USD", symbol: "$",   label: "US Dollar" },
  { code: "EUR", symbol: "€",   label: "Euro" },
  { code: "GBP", symbol: "£",   label: "British Pound" },
  { code: "AUD", symbol: "A$",  label: "Australian Dollar" },
  { code: "CAD", symbol: "C$",  label: "Canadian Dollar" },
  { code: "NZD", symbol: "NZ$", label: "New Zealand Dollar" },
  { code: "ZAR", symbol: "R",   label: "South African Rand" },
  { code: "DKK", symbol: "kr",  label: "Danish Krone" },
  { code: "SEK", symbol: "kr",  label: "Swedish Krona" },
  { code: "NOK", symbol: "kr",  label: "Norwegian Krone" },
  { code: "CZK", symbol: "Kč",  label: "Czech Koruna" },
  { code: "INR", symbol: "₹",   label: "Indian Rupee" },
  { code: "BRL", symbol: "R$",  label: "Brazilian Real" },
  { code: "COP", symbol: "$",   label: "Colombian Peso" },
  { code: "MXN", symbol: "$",   label: "Mexican Peso" },
  { code: "PLN", symbol: "zł",  label: "Polish Złoty" },
  { code: "CHF", symbol: "CHF", label: "Swiss Franc" },
  { code: "ARS", symbol: "AR$", label: "Argentine Peso" },
];

const SUPPORTED_CODES = new Set(SUPPORTED_CURRENCIES.map((c) => c.code));

interface CurrencyContextValue {
  currency: string;
  setCurrency: (code: string) => Promise<boolean>;
  currencySaving: boolean;
  refreshCurrency: () => Promise<void>;
  list: Currency[];
  /**
   * Task #2444 — BCP-47 format locale derived from the URL market so
   * downstream consumers can drive Intl.NumberFormat directly without
   * re-deriving it. e.g. `da-DK` on `/dk/...`, `de-DE` on `/de/...`,
   * `en-GB` on `/uk/...`, `en-US` on worldwide by default.
   */
  formatLocale: string;
  format: (amount: number) => string;
  /**
   * Task #2444 — convenience that takes an explicit currency code and
   * formats it using the active locale. Used by call sites that
   * already have a non-preferred currency in hand (e.g. trip-local
   * currency on host surfaces) but still want market-correct
   * separators.
   */
  formatMoney: (amount: number | null | undefined, currency?: string) => string;
  /**
   * Task #2342 — render an amount converted into the active preferred
   * currency. Browsing surfaces (search, car cards, listing detail)
   * consume this so renters always see prices in their chosen
   * currency. When `fromCurrency` matches the active currency or no
   * conversion rate is available, the source amount is rendered as-is
   * in `fromCurrency` so the renter still sees a real number rather
   * than a blank.
   */
  formatPreferred: (amount: number | null | undefined, fromCurrency: string | null | undefined) => string | null;
  /**
   * Task #2342 — dual-currency disclosure for checkout / confirmation.
   * Returns null when the two currencies match so callers can render a
   * single line.
   */
  formatChargeDisclosure: (
    chargeAmount: number,
    chargeCurrency: string,
  ) => { preferred: string; charge: string } | null;
  /**
   * @deprecated Task #2342 — `≈ {converted}` browsing line removed in
   * favour of single-currency display via `formatPreferred`. Kept as a
   * no-op return so legacy call-sites compile until they're swept.
   */
  formatApprox: (amount: number | null | undefined, fromCurrency: string | null | undefined) => string | null;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function detectInitialCurrency(marketDefault = "USD"): string {
  if (typeof window === "undefined") return marketDefault;
  try {
    const stored = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored && SUPPORTED_CODES.has(stored)) return stored;
  } catch { /* noop */ }
  // An explicit country URL supplies its currency from the first render.
  // Geo hints and remembered markets still cannot change worldwide USD.
  return marketDefault;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { market: urlMarket, language: urlLanguage } = useLocale();
  const { i18n } = useTranslation();
  // Country-root SSR bootstrap: the market's own currency, never a stored one.
  const ssrBootstrap = useSsrBootstrap();
  const [guestCurrency, setCurrencyState] = useState<string>(() =>
    ssrBootstrap
      ? (urlMarket && MARKETS_BY_CODE[urlMarket]?.defaultCurrency) || "USD"
      : detectInitialCurrency((urlMarket && MARKETS_BY_CODE[urlMarket]?.defaultCurrency) || "USD"),
  );
  const userOverride = useRef<boolean>(false);
  const auth = useContext(AuthContext);
  // A guest reservation principal is not a signed-in customer's preference
  // account. Preserve the visitor's chosen currency through guest setup.
  const guestReservation = auth?.session?.user.app_metadata?.checkout_guest === true;
  const subject = guestReservation ? null : auth?.session?.user.id ?? null;
  const token = guestReservation ? undefined : auth?.session?.access_token;
  const accountRef = useRef(subject);
  const readSequence = useRef(0);
  const revision = useRef(0);
  const [account, setAccount] = useState<{ subject: string; explicit: string | null; country: string | null } | null>(null);
  const saving = useRef<string | null>(null);
  const [savingSubject, setSavingSubject] = useState<string | null>(null);
  if (accountRef.current !== subject) {
    accountRef.current = subject;
    revision.current++;
    saving.current = null;
    setAccount(null);
    setSavingSubject(null);
  }
  const accountMarket = countryToMarket(
    account?.subject === subject ? account.country : auth?.profile?.country_code,
  );
  const fallback = (urlMarket && MARKETS_BY_CODE[urlMarket]?.defaultCurrency) || "USD";
  // Never borrow guest or previous-account preferences while settings hydrate.
  const currency = subject
    ? (account?.subject === subject ? account.explicit : null) ??
      (accountMarket && MARKETS_BY_CODE[accountMarket]?.defaultCurrency) ?? fallback
    : guestCurrency;

  const refreshCurrency = useCallback(async () => {
    if (!subject || !token) return;
    const request = ++readSequence.current;
    const originalRevision = revision.current;
    await fetchUserSettings(token).then(data => {
      if (request !== readSequence.current || accountRef.current !== subject || originalRevision !== revision.current || saving.current === subject) return;
      const code = data.preferred_currency?.trim().toUpperCase();
      setAccount({ subject, explicit: code && SUPPORTED_CODES.has(code) ? code : null, country: data.country_code });
    }).catch(() => { /* Retain this account's default; never inherit another account's choice. */ });
  }, [subject, token]);
  useEffect(() => {
    void refreshCurrency();
    return () => { readSequence.current++; };
  }, [refreshCurrency]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
      if (stored && SUPPORTED_CODES.has(stored)) {
        userOverride.current = true;
        setCurrencyState(stored);
      }
    } catch { /* storage may be disabled */ }
  }, [ssrBootstrap]);

  // One persistence owner for every selector; derived defaults never write.
  const setCurrency = useCallback(async (code: string): Promise<boolean> => {
    if (!SUPPORTED_CODES.has(code) || (subject && saving.current === subject)) return false;
    const selectionRevision = ++revision.current;
    if (subject && token) {
      const previous = account?.subject === subject ? account : null;
      const country = previous?.country ?? auth?.profile?.country_code ?? null;
      saving.current = subject;
      setSavingSubject(subject);
      setAccount({ subject, explicit: code, country });
      try {
        await updateUserSettings(token, { preferred_currency: code });
        return accountRef.current === subject && revision.current === selectionRevision;
      } catch {
        if (accountRef.current === subject && revision.current === selectionRevision) setAccount(previous);
        return false;
      } finally {
        if (accountRef.current === subject && revision.current === selectionRevision) {
          revision.current++;
          saving.current = null;
          setSavingSubject(null);
        }
      }
    }
    userOverride.current = true;
    setCurrencyState(code);
    try { window.localStorage.setItem(CURRENCY_STORAGE_KEY, code); } catch { /* noop */ }
    document.cookie = `${CURRENCY_STORAGE_KEY}=${encodeURIComponent(code)}; path=/; max-age=${60*60*24*365}; SameSite=Lax`;
    return true;
  }, [subject, token, account, auth?.profile?.country_code]);

  useEffect(() => {
    if (userOverride.current) return;
    const def = urlMarket ? MARKETS_BY_CODE[urlMarket]?.defaultCurrency : "USD";
    if (def && SUPPORTED_CODES.has(def) && def !== guestCurrency) {
      setCurrencyState(def);
    }
  }, [urlMarket, guestCurrency]);

  // Geo hints belong to the existing region suggestion, not currency defaults.
  // Accepting a region uses its URL currency; selecting a currency or hydrating
  // an account preference calls setCurrency and preserves that explicit choice.

  useEffect(() => {
    document.documentElement.dataset.currency = currency;
  }, [currency]);

  const value = useMemo<CurrencyContextValue>(() => {
    // Task #2444 — derive the format locale from the URL market so
    // prices use Danish / German / Swedish conventions on those
    // markets. Worldwide / unknown falls back to `en-GB`.
    const formatLocale = marketToFormatLocale(urlMarket, urlMarket ? urlLanguage : i18n.language);

    const format = (amount: number) => formatPriceCode(amount, currency, formatLocale);

    const fmtMoney = (amount: number | null | undefined, cur?: string) => {
      const c = (cur || currency || "USD").toUpperCase();
      return formatMoneyWithCurrency(Number(amount ?? 0), c, { locale: formatLocale });
    };

    const formatPreferred = (
      amount: number | null | undefined,
      fromCurrency: string | null | undefined,
    ): string | null => {
      if (amount == null || !Number.isFinite(Number(amount))) return null;
      const from = String(fromCurrency || "").toUpperCase();
      if (!from) return null;
      if (from === currency) return formatPriceCode(Number(amount), currency, formatLocale);
      const converted = fxConvert(Number(amount), from, currency);
      if (converted == null) {
        // No FX rate — render in the source currency so the renter still
        // sees a real number rather than a blank price.
        return formatPriceCode(Number(amount), from, formatLocale);
      }
      return formatPriceCode(Math.round(converted), currency, formatLocale);
    };

    const formatChargeDisclosure = (
      chargeAmount: number,
      chargeCurrency: string,
    ) => {
      const charge = String(chargeCurrency || "").toUpperCase();
      if (!charge || charge === currency) return null;
      const preferredAmt = fxConvert(chargeAmount, charge, currency);
      if (preferredAmt == null) return null;
      return baseFormatChargeDisclosure(
        Math.round(preferredAmt),
        currency,
        chargeAmount,
        charge,
        formatLocale,
      );
    };

    return {
      currency,
      setCurrency,
      currencySaving: Boolean(subject && savingSubject === subject),
      refreshCurrency,
      list: SUPPORTED_CURRENCIES,
      formatLocale,
      format,
      formatMoney: fmtMoney,
      formatPreferred,
      formatChargeDisclosure,
      formatApprox: () => null,
    };
  }, [currency, setCurrency, refreshCurrency, subject, savingSubject, urlMarket, urlLanguage, i18n.language]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    return {
      currency: "USD",
      setCurrency: async () => false,
      currencySaving: false,
      refreshCurrency: async () => {},
      list: SUPPORTED_CURRENCIES,
      formatLocale: "en-GB",
      format: (a) => formatMoneyWithCurrency(a, "USD"),
      formatMoney: (a, cur) => formatMoneyWithCurrency(Number(a ?? 0), (cur || "USD").toUpperCase()),
      formatPreferred: () => null,
      formatChargeDisclosure: () => null,
      formatApprox: () => null,
    };
  }
  return ctx;
}
